/**
 * Public Trophy Room Page
 *
 * Displays a user's public Trophy Room with profile, social links, wallets, and NFC products.
 * No authentication required - public viewer interface.
 *
 * Architecture: Strict MVVM pattern
 * - Page component is pure view (zero business logic)
 * - All logic in usePublicTrophyRoomViewModel
 * - Single useEffect calling onMount() from ViewModel
 *
 * Responsive Design:
 * - Uses standard ANYW3AR pattern: container mx-auto px-8 py-8 w-full md:max-w-[43.75rem]
 * - Mobile (< 768px): Full width, 1 column grid
 * - Tablet+ (>= 768px): 700px max-width, 2 column grid
 * - Maintains virtualization for performance
 * - Consistent container across loading/error/empty/success states
 */

import * as React from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { usePublicTrophyRoomViewModel } from "@/viewModels/usePublicTrophyRoomViewModel";
import { Avatar } from "@/ui/components/ui/avatar";
import { SocialLinkField } from "@/ui/components/ui/social-link-field";
import { ErrorState } from "@/ui/components";
import { PublicCollectionItem } from "@/ui/components/PublicCollectionItem";
import { PublicCollectionItemSkeleton } from "@/ui/components/ui/PublicCollectionItemSkeleton";
import { TrophyRoomEmptyState } from "@/ui/components/TrophyRoomEmptyState";
import { AppLayout } from "@/ui/layouts";
import {
  CollectionSwitch,
  type CollectionFilter,
} from "@/ui/components/collection-switch";
import {
  EmailIcon,
  PhoneIcon,
  XIcon,
  InstagramIcon,
  LinkedInIcon,
  TelegramIcon,
  MyPlaylistIcon,
} from "@/ui/components/icons/social";
import { WalletExpandable } from "@/ui/components/ui/wallet-expandable";

/**
 * Custom hook for responsive media queries
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

// Social link platform order (matches SocialLinksSection.tsx)
const PLATFORM_ORDER = [
  "email",
  "phone",
  "twitter",
  "instagram",
  "linkedin",
  "telegram",
  "myplaylist",
] as const;

// Social link icon mapping
const SOCIAL_ICON_MAP = {
  email: EmailIcon,
  phone: PhoneIcon,
  twitter: XIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  telegram: TelegramIcon,
  myplaylist: MyPlaylistIcon,
} as const;

/**
 * Map social platform to external link URL
 */
function getSocialLinkHref(platform: string, url: string): string {
  if (platform === "email") {
    return url.startsWith("mailto:") ? url : `mailto:${url}`;
  }
  if (platform === "phone") {
    return url.startsWith("tel:") ? url : `tel:${url}`;
  }
  return url;
}

/**
 * Map wallet type from backend (ETH, SOL, BTC) to WalletExpandable component type
 */
function mapWalletType(type: string): "ETH" | "SOL" | "BTC" {
  const normalized = type.toUpperCase();
  if (normalized === "ETH" || normalized === "SOL" || normalized === "BTC") {
    return normalized as "ETH" | "SOL" | "BTC";
  }
  // Fallback to ETH if unknown type
  return "ETH";
}

/**
 * Map CollectionSwitch value ("clothes" | "patch" | null) to FilterMode ("all" | "patches" | "clothing")
 */
function mapCollectionFilterToFilterMode(
  filter: CollectionFilter
): "all" | "patches" | "clothing" {
  if (filter === "patch") return "patches";
  if (filter === "clothes") return "clothing";
  return "all";
}

/**
 * Map FilterMode back to CollectionSwitch value
 */
function mapFilterModeToCollectionFilter(
  mode: "all" | "patches" | "clothing"
): CollectionFilter {
  if (mode === "patches") return "patch";
  if (mode === "clothing") return "clothes";
  return null;
}

export function PublicTrophyRoomPage() {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation();
  const [expandedWalletType, setExpandedWalletType] = React.useState<
    string | null
  >(null);

  // ViewModel - all business logic lives here
  const viewModel = usePublicTrophyRoomViewModel(userId!);
  const { state, actions, computed, onMount } = viewModel;

  // Single useEffect for component lifecycle
  React.useEffect(() => {
    const cleanup = onMount();
    return cleanup;
  }, [onMount]);

  // Responsive column detection
  const isTablet = useMediaQuery("(min-width: 768px)");
  const columnCount = isTablet ? 2 : 1;

  // Calculate row height dynamically based on actual container width
  const getRowHeight = () => {
    // Get actual container width (defaults to max-width if not mounted)
    const containerWidth = parentRef.current?.offsetWidth || 700;

    if (columnCount === 1) {
      // Mobile: 1 column
      // Image = square (width = containerWidth)
      // Gap: 16px (gap-[1rem])
      // Details: 64px (brand + product name rows)
      return containerWidth + 80; // 80 = 16px gap + 64px details
    } else {
      // Tablet: 2 columns with gap-16 (64px between columns)
      const columnWidth = (containerWidth - 64) / 2;
      // Image = square (width = columnWidth)
      // Gap + details: 80px (16px gap + 64px details)
      return columnWidth + 80;
    }
  };

  // Virtualization setup for collection grid (row-based for 2-column support)
  const parentRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(computed.filteredItems.length / columnCount),
    getScrollElement: () => parentRef.current,
    estimateSize: getRowHeight, // Dynamic row height based on layout
    overscan: 2, // Render 2 rows above/below viewport
  });

  // Recalculate row heights when layout changes (mobile ↔ tablet)
  React.useEffect(() => {
    rowVirtualizer.measure();
  }, [columnCount, rowVirtualizer]);

  // --- Loading State ---
  if (state.loading) {
    return (
      <AppLayout>
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading Trophy Room"
        >
          {/* Profile Header Skeleton */}
          <div className="animate-pulse flex flex-col gap-4 items-center">
            {/* Avatar skeleton */}
            <div className="size-24 bg-gray-100 rounded-[0.3125rem]" />
            {/* Username skeleton */}
            <div className="h-6 bg-gray-100 rounded w-3/4" />
            {/* Item count skeleton */}
            <div className="h-4 bg-gray-100 rounded w-1/2" />
          </div>

          {/* Collection Items Skeletons - Responsive Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 mt-8">
            <PublicCollectionItemSkeleton />
            <PublicCollectionItemSkeleton />
            {/* Show second row on tablet+ */}
            <div className="hidden md:block">
              <PublicCollectionItemSkeleton />
            </div>
            <div className="hidden md:block">
              <PublicCollectionItemSkeleton />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // --- Error State ---
  if (state.error) {
    return (
      <AppLayout>
        <ErrorState
          message={
            state.error.includes("not found")
              ? t(
                  "publicTrophyRoom.errors.userNotFound",
                  "User not found. Please check the link and try again."
                )
              : state.error.includes("Network")
              ? t(
                  "publicTrophyRoom.errors.network",
                  "Network error. Please check your connection and try again."
                )
              : t(
                  "publicTrophyRoom.errors.generic",
                  "An error occurred while loading this Trophy Room. Please retry."
                )
          }
          onRetry={actions.refresh}
        />
      </AppLayout>
    );
  }

  // --- Success State: Display Trophy Room ---
  // Note: Empty state is handled inline within the items list section
  return (
    <AppLayout>
      {/* Profile Header */}
      <div className="flex flex-col items-center gap-4 w-full">
        {/* Avatar */}
        <Avatar
          src={state.profile?.avatar}
          alt={state.profile?.username}
          size="xl"
          fallback={
            <span className="text-2xl font-bold">
              {state.profile?.username?.charAt(0).toUpperCase()}
            </span>
          }
        />

        {/* Username */}
        <h1 className="text-2xl font-bold text-gray-900 text-center">
          {state.profile?.username}
        </h1>
      </div>

      {/* Social Links Section */}
      {computed.hasSocialLinks && (
        <div className="flex flex-col gap-2 w-full mt-8">
          {state.socialLinks
            .filter((link) => link.url && link.url.trim() !== "")
            .sort((a, b) => {
              const indexA = PLATFORM_ORDER.indexOf(a.platform as any);
              const indexB = PLATFORM_ORDER.indexOf(b.platform as any);
              return indexA - indexB;
            })
            .map((link) => {
              const IconComponent =
                SOCIAL_ICON_MAP[link.platform as keyof typeof SOCIAL_ICON_MAP];
              if (!IconComponent) return null;

              return (
                <SocialLinkField
                  key={link.platform}
                  leftIcon={<IconComponent size={16} />}
                  text={link.url}
                  href={getSocialLinkHref(link.platform, link.url)}
                />
              );
            })}
        </div>
      )}

      {/* Wallets Section */}
      {computed.hasWallets && (
        <div className="flex flex-col gap-2 w-full mt-8">
          {state.wallets
            .filter((wallet) => {
              const hasAddress =
                wallet.address != null && wallet.address.trim() !== "";
              const hasEnsName =
                wallet.ensName != null && wallet.ensName.trim() !== "";
              return hasAddress || hasEnsName;
            })
            .map((wallet) => (
              <WalletExpandable
                key={wallet.type}
                walletType={mapWalletType(wallet.type)}
                variant="field"
                expanded={expandedWalletType === wallet.type}
                onToggle={() =>
                  setExpandedWalletType(
                    expandedWalletType === wallet.type ? null : wallet.type
                  )
                }
                fieldProps={{
                  displayValue: wallet.address || wallet.ensName || "",
                  fullValue: wallet.address || wallet.ensName || "",
                }}
              />
            ))}
        </div>
      )}

      {/* Collection Filter - Always visible */}
      <div className="w-full mt-8">
        <CollectionSwitch
          value={mapFilterModeToCollectionFilter(state.filterMode)}
          onChange={(filter) => {
            const mode = mapCollectionFilterToFilterMode(filter);
            actions.setFilterMode(mode);
          }}
        />
      </div>

      {/* Collection Grid (Virtualized) */}
      {computed.filteredItemCount > 0 ? (
        <div
          ref={parentRef}
          className="w-full overflow-auto mt-8"
          style={{ minHeight: "600px", maxHeight: "calc(100vh - 20rem)" }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const startIndex = virtualRow.index * columnCount;
              const rowItems = computed.filteredItems.slice(
                startIndex,
                startIndex + columnCount
              );

              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 w-full">
                    {rowItems.map((item) => (
                      <PublicCollectionItem
                        key={item.url}
                        productImage={item.product?.imageUrl || ""}
                        productName={item.product?.title || ""}
                        productNumber={item.itemNumber ?? 0}
                        totalProductNumber={item.product?.editionSize ?? 0}
                        shopifyUrl={`https://anyw3ar.com/`}
                        partnerLink={
                          item.partnerLink
                            ? { url: item.partnerLink, label: "PARTNER'S LINK" }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // Empty state - shown when no items (below filter)
        <div className="w-full mt-8">
          <TrophyRoomEmptyState variant="viewer" />
        </div>
      )}
    </AppLayout>
  );
}
